import { useQuery } from '@tanstack/react-query';
import { useProject } from '@/contexts/ProjectContext';
import { Link } from 'react-router';
import { AlertCircle } from 'lucide-react';

/**
 * OutcomeHealthPanel
 *
 * Renders rollup stats for the selected project:
 * - Total outcomes
 * - Active outcomes
 * - Average convergence score
 * - Blocker count (alerts user if present)
 *
 * Data from GET /api/codebases/:id/outcomes/rollup
 */
export function OutcomeHealthPanel(): React.ReactElement | null {
  const { selectedProjectId } = useProject();

  const { data: rollup, isLoading } = useQuery({
    queryKey: ['outcomes-rollup', selectedProjectId],
    queryFn: async () => {
      // Call the rollup endpoint
      const response = await fetch(`/api/codebases/${selectedProjectId}/outcomes/rollup`);
      if (!response.ok) throw new Error('Failed to fetch rollup');
      return response.json() as Promise<{
        totalOutcomes: number;
        activeOutcomes: number;
        blockerCount: number;
        avgConvergence: number | null;
      }>;
    },
    enabled: !!selectedProjectId,
    staleTime: 10_000,
    refetchInterval: 10_000,
  });

  const convergenceColor =
    !rollup?.avgConvergence || rollup.avgConvergence < 70
      ? 'text-error'
      : rollup.avgConvergence < 90
        ? 'text-warning'
        : 'text-success';

  // Hide the panel entirely when no project is selected — the outer page
  // already renders the "Choose a project above" prompt; duplicating a
  // "Loading health metrics..." string here makes the empty state look
  // broken (the sidebar stays spinning forever against a never-fetched
  // query).
  if (!selectedProjectId) return null;
  if (isLoading) {
    return <div className="text-xs text-text-secondary">Loading health metrics...</div>;
  }

  if (!rollup) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
        Outcome Health
      </div>

      {/* Stats rows */}
      <div className="space-y-1.5 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-text-secondary">Total</span>
          <span className="font-semibold text-text-primary">{rollup.totalOutcomes}</span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-text-secondary">Active</span>
          <span className="font-semibold text-text-primary">{rollup.activeOutcomes}</span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-text-secondary">Avg Convergence</span>
          <span className={`font-semibold ${convergenceColor}`}>
            {rollup.avgConvergence ? `${rollup.avgConvergence.toFixed(0)}%` : '—'}
          </span>
        </div>
      </div>

      {/* Blocker alert */}
      {rollup.blockerCount > 0 && (
        <div className="mt-2 rounded-md border border-warning bg-warning/5 p-2">
          <Link
            to="/delivery"
            className="flex items-center gap-1.5 text-xs text-warning transition-opacity hover:opacity-75"
          >
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="font-medium">
              {rollup.blockerCount} Blocker{rollup.blockerCount > 1 ? 's' : ''}
            </span>
          </Link>
        </div>
      )}
    </div>
  );
}
